import { AlertTriangle, Inbox, Lock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export const PageHeader = ({ title, subtitle, actions, eyebrow }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6">
    <div>
      {eyebrow && <div className="g39-label mb-1.5 text-gold">{eyebrow}</div>}
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const EmptyState = ({ title, body, action, testId = "empty-state" }) => (
  <div data-testid={testId} className="flex flex-col items-center justify-center text-center py-14 px-6">
    <div className="h-11 w-11 rounded-full bg-accent flex items-center justify-center mb-4">
      <Inbox className="h-5 w-5 text-gold" />
    </div>
    <div className="font-display font-semibold">{title}</div>
    {body && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{body}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div data-testid="error-state" className="flex flex-col items-center text-center py-12 px-6">
    <AlertTriangle className="h-6 w-6 text-red-500 mb-3" />
    <div className="font-display font-semibold">Could not load data</div>
    <p className="text-sm text-muted-foreground mt-1 max-w-md">{message}</p>
    {onRetry && (
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry} data-testid="retry-button">
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
      </Button>
    )}
  </div>
);

export const NoAccess = ({ perm }) => (
  <div data-testid="no-access-state" className="g39-card flex flex-col items-center text-center py-16 px-6">
    <Lock className="h-6 w-6 text-gold mb-3" />
    <div className="font-display font-semibold">You don't have access to this page</div>
    <p className="text-sm text-muted-foreground mt-1">Required permission: <span className="font-mono-g">{perm}</span></p>
  </div>
);

export const LoadingRows = ({ rows = 5 }) => (
  <div className="p-5 space-y-3" data-testid="loading-state">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
  </div>
);

const TONES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  invited: "bg-sky-50 text-sky-700 border-sky-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  inactive: "bg-stone-100 text-stone-600 border-stone-200",
  suspended: "bg-red-50 text-red-700 border-red-200",
  demo: "bg-amber-50 text-amber-800 border-amber-300",
  gold: "bg-accent text-gold border-[hsl(var(--gold)/0.35)]",
  neutral: "bg-secondary text-foreground/70 border-border",
};

export const Badge = ({ tone = "neutral", children, className, ...rest }) => (
  <span {...rest} className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap", TONES[tone] || TONES.neutral, className)}>
    {children}
  </span>
);

export const Field = ({ label, children, hint, className }) => (
  <label className={cn("block space-y-1.5", className)}>
    <span className="g39-label">{label}</span>
    {children}
    {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
  </label>
);
